// src/components/gavion/domain/dialer/outcomes.ts
import {
  nextCallingWindow,
  weekdayCallback,
  withinCallingHours,
  type CallingHours,
} from "../time";
import type { CallOutcome, DialOutcome } from "./types";

export type OutcomeTone =
  | "neutral"
  | "orange-outline"
  | "orange-solid"
  | "grey"
  | "destructive-outline";
export type OutcomeDef = {
  code: DialOutcome;
  hotkey: string;
  tone: OutcomeTone;
  reachedHuman: boolean;
};

export const DIAL_OUTCOMES: readonly OutcomeDef[] = [
  { code: "no_answer", hotkey: "1", tone: "neutral", reachedHuman: false },
  { code: "voicemail", hotkey: "2", tone: "neutral", reachedHuman: false },
  {
    code: "talked_callback",
    hotkey: "3",
    tone: "orange-outline",
    reachedHuman: true,
  },
  {
    code: "demo_booked",
    hotkey: "4",
    tone: "orange-solid",
    reachedHuman: true,
  },
  {
    code: "brochure_sent",
    hotkey: "5",
    tone: "orange-outline",
    reachedHuman: true,
  },
  { code: "not_interested", hotkey: "6", tone: "grey", reachedHuman: true },
  { code: "wrong_number", hotkey: "7", tone: "neutral", reachedHuman: false },
  {
    code: "do_not_call",
    hotkey: "8",
    tone: "destructive-outline",
    reachedHuman: false,
  },
];
export const outcomeByHotkey = (n: number) =>
  DIAL_OUTCOMES.find((o) => o.hotkey === String(n));
export const NOT_INTERESTED_REASONS = [
  "already_supplier",
  "no_budget",
  "bad_timing",
  "not_decision_maker",
  "other",
] as const;
export const DEMO_DURATIONS = [30, 45, 60] as const;

export type OutcomePayload = {
  call_id: number;
  version: number;
  outcome: CallOutcome;
  note?: string;
  callback_at?: string;
  demo?: {
    at?: string;
    duration_min?: number;
    attendee_email?: string;
    /** GAVION demo-contact: « Nom du participant » (SQL: the new contact's name, 68_gavion_demo_contact.sql). */
    attendee_name?: string;
    amount?: number;
  };
  email?: string;
  reason?: string;
  reason_detail?: string;
  dnc_confirmed?: boolean;
};
export type OutcomeErrorCode =
  | "outcome_invalid"
  | "callback_required"
  | "callback_in_past"
  | "callback_outside_hours"
  | "callback_weekend" // GAVION missed-call: dialer only (SQL moves such a callback instead)
  | "demo_datetime_required"
  | "demo_duration_invalid"
  | "email_required"
  | "reason_required"
  | "confirmation_required";
export type OutcomeValidation =
  | { ok: true }
  | { ok: false; code: OutcomeErrorCode; field: string; suggestion?: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fail = (
  code: OutcomeErrorCode,
  field: string,
  suggestion?: string,
): OutcomeValidation =>
  suggestion
    ? { ok: false, code, field, suggestion }
    : { ok: false, code, field };

/**
 * GAVION missed-call (owner request 2026-10-15): the dialer refuses a callback on a Saturday, a Sunday, Dec 25 or
 * Jan 1 (lead-local date) and suggests the next business day at the same time. Dialer-side only: SQL
 * (private.weekday_callback, 55) MOVES such a callback rather than refusing it, so validateOutcome (the SQL mirror used
 * by the demo API too) does not include it.
 */
export function validateCallbackDay(
  p: OutcomePayload,
  leadTimezone: string,
): OutcomeValidation {
  if (p.outcome !== "talked_callback" || !p.callback_at) return { ok: true };
  const at = new Date(p.callback_at);
  if (Number.isNaN(at.getTime())) return { ok: true };
  const moved = weekdayCallback(at, leadTimezone);
  return moved.getTime() === at.getTime()
    ? { ok: true }
    : fail("callback_weekend", "callback_at", moved.toISOString());
}

/** Same rules and codes as SQL log_call_outcome (spec 04 §3). */
export function validateOutcome(
  p: OutcomePayload,
  ctx: {
    now: Date;
    leadTimezone: string;
    leadEmail: string | null;
    hours: CallingHours;
  },
): OutcomeValidation {
  switch (p.outcome) {
    case "unlogged": // set by the system for orphan calls; never submitted by a rep
      return fail("outcome_invalid", "outcome");
    case "talked_callback": {
      if (!p.callback_at || Number.isNaN(Date.parse(p.callback_at)))
        return fail("callback_required", "callback_at");
      const at = new Date(p.callback_at);
      if (at.getTime() <= ctx.now.getTime())
        return fail("callback_in_past", "callback_at");
      if (!withinCallingHours(at, ctx.leadTimezone, ctx.hours)) {
        return fail(
          "callback_outside_hours",
          "callback_at",
          nextCallingWindow(at, ctx.leadTimezone, ctx.hours).toISOString(),
        );
      }
      return { ok: true };
    }
    case "demo_booked":
      if (!p.demo?.at || Number.isNaN(Date.parse(p.demo.at)))
        return fail("demo_datetime_required", "demo.at");
      if (
        p.demo.duration_min !== undefined &&
        !(DEMO_DURATIONS as readonly number[]).includes(p.demo.duration_min)
      )
        return fail("demo_duration_invalid", "demo.duration_min");
      return { ok: true };
    case "brochure_sent": {
      const email = (p.email ?? ctx.leadEmail ?? "").trim();
      return EMAIL.test(email) ? { ok: true } : fail("email_required", "email");
    }
    case "not_interested":
      return p.reason &&
        (NOT_INTERESTED_REASONS as readonly string[]).includes(p.reason)
        ? { ok: true }
        : fail("reason_required", "reason");
    case "do_not_call":
      return p.dnc_confirmed
        ? { ok: true }
        : fail("confirmation_required", "dnc_confirmed");
    default:
      return { ok: true };
  }
}
